import React from 'react';
import './App.css';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import TopMenu from './features/navigation/TopMenu';
import VerifyEmailBanner from './features/auth/VerifyEmailBanner';
import { PriorLoginRouteGuard } from './features/auth/PriorLoginRouteGuard';
import AppRoutes from './routes';
import ApiNotificationProvider from './features/common/ApiNotificationProvider';
import PendingGameAnalysisPoll from './features/games/my-games/PendingGameAnalysisPoll';
import PendingImportPoll from './features/games/my-games/PendingImportPoll';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import { darkTheme } from './theme';
import TrialWelcomeSplash from './features/subscription/TrialWelcomeSplash';
import DeployFreshness from './features/version/DeployFreshness';
import { useAuth } from './features/auth/AuthContext';
import { hasLoggedInBefore } from './features/auth/priorLoginCookie';
import { isAnonymousReadablePath } from './features/common/authRedirect';

const AppLayout = () => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  const showNav =
    isLoggedIn ||
    !hasLoggedInBefore() ||
    isAnonymousReadablePath(location.pathname);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100dvh',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      {showNav && <TopMenu />}
      {showNav && <VerifyEmailBanner />}
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          overflowAnchor: 'none',
          overscrollBehavior: 'none',
          touchAction: 'pan-y',
          boxSizing: 'border-box',
          // Trainer / board pages mark themselves with data-app-page-fill so
          // only their inner panels scroll — never this outer main scroller.
          '&:has([data-app-page-fill])': {
            overflowY: 'hidden',
            touchAction: 'manipulation',
          },
        }}
      >
        <PriorLoginRouteGuard>
          <AppRoutes />
        </PriorLoginRouteGuard>
      </Box>
    </Box>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline enableColorScheme />
        <Router
          future={{
            v7_relativeSplatPath: true,
          }}
        >
          <ApiNotificationProvider>
            <TrialWelcomeSplash />
            <DeployFreshness />
            <PendingGameAnalysisPoll />
            <PendingImportPoll />
            <AppLayout />
          </ApiNotificationProvider>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
